import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Layout from "../common/Layout";
import { useAuth } from "../../context/AuthContext";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { getBusinesses } from "../../api/businessApi";
import { getTerminals } from "../../api/terminalApi";
import { getTransactions } from "../../api/transactionApi";
import { parseDateTime } from "@internationalized/date";

import { DateRangePicker } from "../common/DateRangePicker";

// ---------- normalize text ----------
const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// ---------- deep search ----------
const buildHaystack = (obj, depth = 0, seen = new WeakSet()) => {
  if (obj == null || depth > 12) return "";
  const t = typeof obj;

  if (t === "string") return obj;
  if (t === "number" || t === "boolean" || t === "bigint") return String(obj);
  if (t === "function" || t === "symbol") return "";

  if (t === "object") {
    if (obj instanceof Date) return obj.toISOString();

    if (seen.has(obj)) return "";
    seen.add(obj);

    if (Array.isArray(obj)) {
      let out = "";
      for (let i = 0; i < obj.length; i++) {
        try {
          out += " " + buildHaystack(obj[i], depth + 1, seen);
        } catch {
          /* skip */
        }
      }
      return out;
    }

    let out = "";
    let keys = [];
    try {
      keys = Object.keys(obj);
    } catch {
      return "";
    }

    for (const k of keys) {
      out += " " + k;
      try {
        out += " " + buildHaystack(obj[k], depth + 1, seen);
      } catch {
        /* skip */
      }
    }
    return out;
  }
  return "";
};

// ---------- JS Date -> CalendarDateTime (for the picker) ----------
const dateToCalendarDateTime = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const str = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
  return parseDateTime(str);
};

// ---------- CalendarDateTime -> JS Date ----------
const calendarDateTimeToDate = (cdt) => {
  if (!cdt) return null;
  return new Date(
    cdt.year,
    cdt.month - 1,
    cdt.day,
    cdt.hour ?? 0,
    cdt.minute ?? 0,
    cdt.second ?? 0,
    cdt.millisecond ?? 0,
  );
};

// ---------- local ISO WITHOUT timezone suffix ----------
const toLocalIso = (date) => {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
};

// ---------- LOCAL TIME helpers (browser's timezone) ----------
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

// Preset range calculator — uses LOCAL time only
const computePresetRange = (preset) => {
  const now = new Date();
  let from = new Date(now);
  let to = new Date(now);

  switch (preset) {
    case "today":
      break;
    case "yesterday":
      from.setDate(now.getDate() - 1);
      to.setDate(now.getDate() - 1);
      break;
    case "thisWeek": {
      const day = now.getDay(); // 0 = Sun, 1 = Mon, ...
      const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday start
      from = new Date(now.getFullYear(), now.getMonth(), diff);
      to = new Date(now);
      break;
    }
    case "thisMonth":
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = new Date(now);
      break;
    case "thisYear":
      from = new Date(now.getFullYear(), 0, 1);
      to = new Date(now);
      break;
    default:
      return null;
  }

  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  return { from, to };
};

// ---------- component ----------
const Transactions = () => {
  const { logout } = useAuth();
  const [activeMenu, setActiveMenu] = useState("transaction");

  const [dateRange, setDateRange] = useState("today");
  const [startDate, setStartDate] = useState(() => startOfToday());
  const [endDate, setEndDate] = useState(() => endOfToday());
  const [business, setBusiness] = useState(null);
  const [terminal, setTerminal] = useState(null);

  // date validation error
  const [dateError, setDateError] = useState("");

  const [businessOptions, setBusinessOptions] = useState([]);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [terminalOptions, setTerminalOptions] = useState([]);
  const [terminalLoading, setTerminalLoading] = useState(false);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [txnSearch, setTxnSearch] = useState("");
  const [txnStatusFilter, setTxnStatusFilter] = useState("all");

  const requestIdRef = useRef(0);
  const businessesLoadedRef = useRef(false);
  const businessesLoadingRef = useRef(false);

  const rangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "thisWeek", label: "This Week" },
    { value: "thisMonth", label: "This Month" },
    { value: "thisYear", label: "This Year" },
    { value: "customDate", label: "Custom Range" },
  ];

  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  // ---------- Load businesses on mount ----------
  useEffect(() => {
    // Once-only guard — prevents duplicate calls (StrictMode, remounts, etc.)
    if (businessesLoadedRef.current || businessesLoadingRef.current) return;

    let cancelled = false;
    businessesLoadingRef.current = true;

    const loadBusinesses = async () => {
      setBusinessLoading(true);
      try {
        const list = await getBusinesses();
        if (cancelled) return;

        const arr = Array.isArray(list)
          ? list
          : list?.data || list?.items || [];

        const options = arr.map((b) => {
          const guid =
            b.businessGuid ||
            b.BusinessGuid ||
            b.business?.businessGuid ||
            b.business?.BusinessGuid ||
            b.id ||
            b.Id;

          const label =
            b.tradingName ||
            b.TradingName ||
            b.companyName ||
            b.CompanyName ||
            "Unnamed Business";

          let deep = "";
          try {
            deep = buildHaystack(b);
          } catch (e) {
            console.warn("Haystack build failed for business:", guid, e);
          }

          const explicit = [
            b.companyName,
            b.CompanyName,
            b.tradingName,
            b.TradingName,
            b.email,
            b.Email,
            b.emailAddress,
            b.EmailAddress,
            b.phone,
            b.Phone,
            b.phoneNumber,
            b.PhoneNumber,
            b.mobile,
            b.Mobile,
            b.mobileNumber,
            b.MobileNumber,
            b.address,
            b.Address,
            b.addressLine1,
            b.AddressLine1,
            b.addressLine2,
            b.AddressLine2,
            b.street,
            b.Street,
            b.suburb,
            b.Suburb,
            b.city,
            b.City,
            b.state,
            b.State,
            b.postcode,
            b.Postcode,
            b.zip,
            b.Zip,
            b.country,
            b.Country,
            b.acquirerMId,
            b.AcquirerMId,
            b.businessGuid,
            b.BusinessGuid,
            guid,
            label,
          ]
            .filter(Boolean)
            .join(" ");

          const haystack = normalizeText(`${explicit} ${deep}`);

          return {
            value: guid,
            label,
            business: b,
            __haystack: haystack,
          };
        });

        setBusinessOptions(options);
        businessesLoadedRef.current = true;
      } catch (err) {
        if (!cancelled) {
          setError("Failed to load business list.");
        }
      } finally {
        if (!cancelled) setBusinessLoading(false);
        businessesLoadingRef.current = false;
      }
    };

    loadBusinesses();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Load terminals when business changes ----------
  useEffect(() => {
    let cancelled = false;

    if (!business) {
      setTerminalOptions([]);
      setTerminal(null);
      return;
    }

    const businessId = business.value || business.business?.businessGuid;
    if (!businessId) {
      setTerminalOptions([]);
      return;
    }

    const loadTerminals = async () => {
      setTerminalLoading(true);
      try {
        const list = await getTerminals(businessId);
        if (cancelled) return;

        const arr = Array.isArray(list)
          ? list
          : list?.data || list?.items || [];

        const options = arr.map((t, i) => {
          const id =
            t.terminalGuid ||
            t.terminalId ||
            t.acquirerTId ||
            t.id ||
            `terminal-${i}`;
          const label =
            t.tradingName ||
            t.terminalName ||
            t.name ||
            t.acquirerTId ||
            `Terminal ${i + 1}`;

          return {
            value: id,
            label,
            terminal: t,
          };
        });

        setTerminalOptions(options);
      } catch (err) {
        if (!cancelled) {
          setTerminalOptions([]);
        }
      } finally {
        if (!cancelled) setTerminalLoading(false);
      }
    };
    loadTerminals();
    return () => {
      cancelled = true;
    };
  }, [business]);

  // ---------- handlers ----------
  const handleMenuChange = (menu) => {
    setActiveMenu(menu);
    if (menu === "logout") logout();
  };

  // When user picks a preset range (Today / Yesterday / etc.)
  const handleRangeChange = (selected) => {
    setDateRange(selected.value);
    setDateError("");

    if (selected.value === "customDate") {
      // keep existing dates; user will pick them
      return;
    }

    const preset = computePresetRange(selected.value);
    if (!preset) return;

    setStartDate(preset.from);
    setEndDate(preset.to);
  };

  // When user changes the DateRangePicker directly
  // `value` is a { start: CalendarDateTime, end: CalendarDateTime } object
  const handleDateRangePickerChange = (value) => {
    setDateRange("customDate");

    if (!value || !value.start || !value.end) {
      setDateError("Please select both a start and end date/time.");
      return;
    }

    const from = calendarDateTimeToDate(value.start);
    const to = calendarDateTimeToDate(value.end);

    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      setDateError("Invalid date/time format.");
      return;
    }

    if (from > to) {
      setDateError("Start date/time must be before end date/time.");
      return;
    }

    setDateError("");
    setStartDate(from);
    setEndDate(to);
  };

  const handleBusinessChange = (selected) => {
    setBusiness(selected);
    setTerminal(null);
    setTerminalOptions([]);
  };

  // Value object for the picker (controlled)
  const pickerValue = useMemo(() => {
    if (!startDate || !endDate) return null;
    return {
      start: dateToCalendarDateTime(startDate),
      end: dateToCalendarDateTime(endDate),
    };
  }, [startDate, endDate]);

  // ---------- data fetch ----------
  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) {
      setDateError("Start date/time must be before end date/time.");
      return;
    }

    const myId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const payload = {
        start: toLocalIso(startDate),
        finish: toLocalIso(endDate),
        businessGuid: business?.value || null,
        acquirerTId: terminal?.terminal?.acquirerTId || terminal?.value || null,
        // No timezone — backend uses local time
      };

      const data = await getTransactions(payload);

      if (myId !== requestIdRef.current) return;
      setReport(data);
    } catch (err) {
      if (myId !== requestIdRef.current) return;
      setError("Failed to load report. Please try again.");
    } finally {
      if (myId === requestIdRef.current) setLoading(false);
    }
  }, [startDate, endDate, business, terminal]);

  // Fetch whenever the resolved start/end changes (covers both
  // preset changes and picker changes). Debounce a bit for custom.
  useEffect(() => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) return; // don't call API on invalid range

    const delay = dateRange === "customDate" ? 500 : 0;
    const t = setTimeout(() => fetchReport(), delay);
    return () => clearTimeout(t);
  }, [fetchReport, dateRange, startDate, endDate]);

  // ---------- Normalize transactions list ----------
  const transactions = useMemo(() => {
    if (!report) return [];
    if (Array.isArray(report)) return report;
    return (
      report.transactions || report.items || report.data || report.rows || []
    );
  }, [report]);

  // ---------- Filtered transactions ----------
  const filteredTransactions = useMemo(() => {
    let rows = transactions;

    if (txnStatusFilter === "withFee") {
      rows = rows.filter((t) => Number(t.feeValue) > 0);
    } else if (txnStatusFilter === "withTip") {
      rows = rows.filter((t) => Number(t.tipAmount) > 0);
    } else if (txnStatusFilter === "withCashout") {
      rows = rows.filter((t) => Number(t.cashout) > 0);
    }

    if (txnSearch.trim()) {
      const q = normalizeText(txnSearch);
      const terms = q.split(/\s+/).filter(Boolean);
      rows = rows.filter((t) => {
        const hay = normalizeText(buildHaystack(t));
        return terms.every((term) => hay.includes(term));
      });
    }

    return rows;
  }, [transactions, txnSearch, txnStatusFilter]);

  // ---------- BUSINESS deep search filter ----------
  const filterBusinessOption = useCallback((option, inputValue) => {
    if (!inputValue || !inputValue.trim()) return true;

    const data = option.data || option;
    const haystack =
      data.__haystack ||
      option.__haystack ||
      normalizeText(data.label || option.label || "");

    const terms = normalizeText(inputValue).split(/\s+/).filter(Boolean);
    return terms.every((term) => haystack.includes(term));
  }, []);

  const formatBusinessLabel = (option) => {
    const mid = option.business?.acquirerMId;
    return mid ? `(${mid}) ${option.label}` : option.label;
  };

  // ---------- Transactions table columns ----------
  const transactionColumns = useMemo(
    () => [
      {
        name: "TID",
        selector: (row) => row.acquirerTId,
        sortable: true,
        width: "110px",
        cell: (row) => (
          <span className="fw-semibold">{row.acquirerTId || "—"}</span>
        ),
      },
      {
        name: "Trans Date",
        width: "170px",
        selector: (row) => row.transactionDateLocal || row.transactionDate,
        sortable: true,
        cell: (row) => {
          const d = row.transactionDateLocal || row.transactionDate;
          if (!d) return "—";
          const dt = new Date(d);
          return isNaN(dt.getTime())
            ? d
            : dt.toLocaleString(undefined, {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              });
        },
      },
      {
        name: "Trading Name",
        selector: (row) => row.tradingName,
        sortable: true,
        wrap: true,
        grow: 1.5,
        minWidth: "200px",
        cell: (row) => row.tradingName || "—",
      },
      {
        name: "Subtotal",
        width: "110px",
        selector: (row) => Number(row.subtotal) || 0,
        sortable: true,
        right: true,
        cell: (row) => `$${fmt(Number(row.subtotal) || 0)}`,
      },
      {
        name: "Fee",
        width: "220px",
        selector: (row) => Number(row.feeValue) || 0,
        sortable: true,
        right: true,
        cell: (row) => {
          const feeTypeName = row.feeType;
          const hasFee = feeTypeName && feeTypeName !== "None";

          if (!hasFee) {
            return "—";
          }

          const badgeClassMap = {
            "Service Fee": "bg-blaze-orange",
            "Admin Fee": "bg-light-orange",
            "Software Fee": "bg-peach-glow",
          };

          const badgeClass = badgeClassMap[feeTypeName] || "bg-blaze-orange";

          return (
            <div className="d-flex align-items-center justify-content-end gap-2">
              <span className={`badge ${badgeClass}`}>{feeTypeName}</span>
              <span>${fmt(Number(row.feeValue) || 0)}</span>
            </div>
          );
        },
      },
      {
        name: "Surcharge",
        selector: (row) => Number(row.surchargeValue) || 0,
        sortable: true,
        right: true,
        width: "220px",
        cell: (row) => {
          const surchargeType = row.surchargeType;
          const hasSurcharge = surchargeType && surchargeType !== "None";

          if (!hasSurcharge) {
            return "—";
          }

          const badgeClassMap = {
            "Weekend Surcharge": "bg-blaze-orange",
            "Public Holiday Surcharge": "bg-peach-glow",
          };

          const badgeClass = badgeClassMap[surchargeType] || "bg-blaze-orange";

          return (
            <div className="d-flex align-items-center justify-content-end gap-2">
              <span className={`badge ${badgeClass}`}>
                {surchargeType == "Weekend Surcharge"
                  ? "Weekend"
                  : surchargeType == "Public Holiday Surcharge"
                    ? "Public Holiday"
                    : "None"}
              </span>
              <span>${fmt(Number(row.surchargeValue) || 0)}</span>
            </div>
          );
        },
      },
      // {
      //   name: "Cashout",
      //   selector: (row) => Number(row.cashout) || 0,
      //   sortable: true,
      //   right: true,
      //   cell: (row) => `$${fmt(Number(row.cashout) || 0)}`,
      // },
      // {
      //   name: "Tip",
      //   selector: (row) => Number(row.tipAmount) || 0,
      //   sortable: true,
      //   right: true,
      //   cell: (row) => `$${fmt(Number(row.tipAmount) || 0)}`,
      // },
      {
        name: "Total",
        selector: (row) => Number(row.total) || 0,
        sortable: true,
        right: true,
        width: "110px",
        cell: (row) => (
          <span className="fw-bold text-blaze-orange">
            ${fmt(Number(row.total) || 0)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <Layout
      activeMenu={activeMenu}
      onMenuChange={handleMenuChange}
      title="Transaction"
    >
      {/* ---------- Filters ---------- */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4 col-lg-3">
              <label className="form-label small fw-semibold">Date Range</label>
              <Select
                options={rangeOptions}
                value={rangeOptions.find((r) => r.value === dateRange)}
                onChange={handleRangeChange}
                isSearchable={false}
                className="react-select-container small"
                classNamePrefix="react-select"
              />
            </div>

            <div className="col-md-8 col-lg-4">
              <div className="datepicker-wrapper">
                <DateRangePicker
                  label="Select Date &amp; Time Range"
                  granularity="second"
                  hourCycle={24}
                  value={pickerValue}
                  onChange={handleDateRangePickerChange}
                />
                {dateError && (
                  <div className="text-danger small mt-1">{dateError}</div>
                )}
              </div>
            </div>

            <div className="col-md-4 col-lg-3">
              <label className="form-label small fw-semibold">Business</label>
              <Select
                options={businessOptions}
                value={business}
                onChange={handleBusinessChange}
                isLoading={businessLoading}
                isClearable
                isSearchable
                placeholder={
                  businessLoading ? "Loading businesses…" : "Search…"
                }
                className="react-select-container small"
                classNamePrefix="react-select"
                filterOption={filterBusinessOption}
                formatOptionLabel={(option) => formatBusinessLabel(option)}
              />
            </div>

            {business && (
              <div className="col-md-4 col-lg-2">
                <label className="form-label small fw-semibold">Terminal</label>
                <Select
                  options={terminalOptions}
                  value={terminal}
                  onChange={setTerminal}
                  isLoading={terminalLoading}
                  isDisabled={terminalLoading}
                  isClearable
                  isSearchable
                  placeholder={
                    terminalLoading ? "Loading terminals…" : "All Terminals"
                  }
                  className="react-select-container small"
                  classNamePrefix="react-select"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* ---------- Transactions Table ---------- */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h6 className="fw-bold mb-0">Transactions</h6>
            <div className="d-flex gap-2">
              {/* <select
                className="form-select form-select-sm"
                value={txnStatusFilter}
                onChange={(e) => setTxnStatusFilter(e.target.value)}
              >
                <option value="all">All transactions</option>
                <option value="withFee">With fee</option>
                <option value="withTip">With tip</option>
                <option value="withCashout">With cashout</option>
              </select> */}
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ minWidth: "260px" }}
                placeholder="Search transactions…"
                value={txnSearch}
                onChange={(e) => setTxnSearch(e.target.value)}
              />
            </div>
          </div>

          <DataTable
            columns={transactionColumns}
            data={filteredTransactions}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            highlightOnHover
            striped
            responsive
            persistTableHead
            progressPending={loading}
            className="transction-table"
            noDataComponent={
              <div className="text-center py-5 text-theme-muted">
                No transactions for this period.
              </div>
            }
          />
        </div>
      </div>
    </Layout>
  );
};

export default Transactions;
